import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { api } from '../lib/api';
import type { Policy } from '../lib/api';
import {
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  Panel,
  formatDateTime,
  useAsync,
} from '../components/ui';
import { MetricCard } from '../components/chrome';
import { IconCheck, IconClock, IconCopy, IconDownload, IconX } from '../components/icons';

interface EnrollmentResult {
  enrollmentId: string;
  token: string;
  serverUrl: string;
  expiresAt: string;
  qrDataUrl: string;
  qrPayload: Record<string, unknown>;
  instructions: string[];
  adbInstructions: string[];
  qrReady: boolean;
  qrBlockedReason: string | null;
  wifiIncluded: boolean;
  wifiWarning: string | null;
}

interface EnrollmentRow {
  id: string;
  tokenPreview: string;
  status: string;
  one_time_only: boolean;
  expires_at: string;
  consumed_at: string | null;
  created_at: string;
  policy_name: string | null;
  device_name: string | null;
  created_by_email: string | null;
}

export default function EnrollmentPage() {
  const [policyId, setPolicyId] = useState('');
  const [ttl, setTtl] = useState(60);
  const [result, setResult] = useState<EnrollmentResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<'server' | 'token' | null>(null);
  const [tokenQuery, setTokenQuery] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTyped, setDeleteTyped] = useState('');

  const copy = (value: string, which: 'server' | 'token') => {
    void navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(which);
        setTimeout(() => setCopied(null), 2000);
      },
      () => undefined,
    );
  };

  const downloadAgentApk = (event: MouseEvent<HTMLAnchorElement>) => {
    // The endpoint is cacheable for five minutes so Android can retry a failed
    // download mid-provisioning. An administrator clicking this straight after
    // a redeploy must still receive the build that was just shipped, so the
    // click always requests a URL no cache has seen.
    event.preventDefault();
    window.location.href = `/api/dpc/agent.apk?t=${Date.now()}`;
  };

  const policies = useAsync((signal) => api.get<{ policies: Policy[] }>('/policies', signal), []);
  const list = useAsync(
    (signal) => api.get<{ enrollments: EnrollmentRow[] }>('/enrollments', signal),
    [],
    20_000,
  );

  const rows = list.data?.enrollments ?? [];
  const counts = useMemo(() => ({
    pending: rows.filter((e) => e.status === 'pending').length,
    enrolled: rows.filter((e) => e.status === 'enrolled').length,
    expired: rows.filter((e) => e.status === 'expired' || e.status === 'revoked').length,
  }), [rows]);

  const filtered = rows.filter((e) => {
    if (!tokenQuery.trim()) return true;
    const q = tokenQuery.toLowerCase();
    return (
      e.tokenPreview.toLowerCase().includes(q) ||
      (e.policy_name ?? '').toLowerCase().includes(q) ||
      (e.device_name ?? '').toLowerCase().includes(q)
    );
  });

  const generate = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const created = await api.post<EnrollmentResult>('/enrollments', {
        policyId: policyId || null,
        ttlMinutes: ttl,
        oneTimeOnly: true,
      });
      setResult(created);
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('Revoke this enrollment token? A device that has not used it yet will no longer be able to enroll.')) return;
    try {
      await api.post(`/enrollments/${id}/revoke`);
      list.reload();
    } catch (err) {
      setError(err);
    }
  };

  const tokenIsExpired = (e: EnrollmentRow) =>
    e.status === 'expired' || e.status === 'revoked' || new Date(e.expires_at).getTime() <= Date.now();

  const removeToken = async (e: EnrollmentRow) => {
    setError(null);
    try {
      if (tokenIsExpired(e)) {
        if (!window.confirm('Remove this expired token from the list?')) return;
        await api.delete(`/enrollments/${e.id}`);
      } else {
        if (deleteId !== e.id || deleteTyped.trim().toLowerCase() !== 'delete') {
          setDeleteId(e.id);
          setDeleteTyped('');
          return;
        }
        await api.delete(`/enrollments/${e.id}`, { confirm: 'delete' });
        setDeleteId(null);
        setDeleteTyped('');
      }
      list.reload();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Enrollment</h2>
          <p>Generate a token and provision a tablet as a fully managed device.</p>
        </div>
        <a
          className="button"
          href="/api/dpc/agent.apk"
          download="tab-dpc.apk"
          onClick={downloadAgentApk}
          title="The management agent currently hosted on this server — the same build a tablet installs during QR provisioning."
        >
          <IconDownload /> Download agent APK
        </a>
      </div>

      <ErrorNotice error={error} />

      <Notice kind="warning">
        <strong>A tablet must be factory reset before it can become a Device Owner.</strong>
        <div>
          Android only allows Device Owner provisioning during the out-of-box setup wizard, and
          only on a device with no Google account added. This is an Android restriction, not a
          limitation of this system. A reset erases everything on the tablet.
        </div>
      </Notice>

      <div className="grid cols-2">
        <Panel title="Generate enrollment token">
          <label>
            <span>Initial policy</span>
            <select value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
              <option value="">No policy (device enrolls unmanaged)</option>
              {(policies.data?.policies ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.kind})
                </option>
              ))}
            </select>
          </label>
          <p className="small muted" style={{ marginTop: -6 }}>
            Start with <strong>TEST_POLICY</strong>. It is deliberately escapable — Wi-Fi,
            factory reset and USB debugging all stay available — so the device can be recovered
            by hand if anything goes wrong.
          </p>

          <label>
            <span>Token lifetime (minutes)</span>
            <div className="field-icon">
              <IconClock />
              <input
                type="number"
                min={5}
                max={10080}
                value={ttl}
                onChange={(e) => setTtl(Number(e.target.value))}
              />
            </div>
          </label>

          <button className="primary" onClick={generate} disabled={busy}>
            {busy ? 'Generating…' : 'Generate token & QR code'}
          </button>
        </Panel>

        <Panel title="Scan this on the tablet">
          {!result ? (
            <Empty title="No token generated yet.">
              Choose a policy and generate a QR code. It appears here once, and cannot be retrieved later.
            </Empty>
          ) : (
            <>
              {!result.qrReady && (
                <Notice kind="warning">
                  <strong>QR provisioning is not ready yet.</strong>
                  <div>{result.qrBlockedReason}</div>
                </Notice>
              )}
              {result.wifiWarning && <Notice kind="info">{result.wifiWarning}</Notice>}

              <div className="qr-row">
                <img className="qr" src={result.qrDataUrl} alt="Enrollment QR code" />
                <div className="actions" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <a className="button" href={result.qrDataUrl} download="enrollment-qr.png">
                    <IconDownload /> Download QR
                  </a>
                  <button type="button" onClick={() => copy(result.token, 'token')}>
                    <IconCopy /> {copied === 'token' ? 'Copied' : 'Copy token'}
                  </button>
                  <p className="small muted" style={{ margin: 0 }}>
                    Expires {formatDateTime(result.expiresAt)}
                  </p>
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>

      <div className="metric-row three">
        <MetricCard tone="blue" icon={<IconClock />} label="Pending" value={counts.pending} />
        <MetricCard tone="green" icon={<IconCheck />} label="Enrolled" value={counts.enrolled} />
        <MetricCard tone="red" icon={<IconX />} label="Expired" value={counts.expired} />
      </div>

      <Panel
        title="Enrollment tokens"
        actions={
          <input
            type="search"
            value={tokenQuery}
            onChange={(e) => setTokenQuery(e.target.value)}
            placeholder="Search tokens…"
            aria-label="Search tokens"
            style={{ maxWidth: 240 }}
          />
        }
      >
        {list.loading && !list.data ? (
          <Loading what="tokens" />
        ) : filtered.length === 0 ? (
          <Empty>No enrollment tokens have been generated.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Token</th><th>Status</th><th>Policy</th><th>Device</th>
                  <th>Expires</th><th>Created by</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id}>
                    <td className="mono">{e.tokenPreview}</td>
                    <td>
                      <span className={`badge ${
                        e.status === 'enrolled' ? 'ok'
                        : e.status === 'pending' ? 'info'
                        : e.status === 'expired' || e.status === 'revoked' ? 'muted' : 'danger'
                      }`}>
                        {e.status.toUpperCase()}
                      </span>
                    </td>
                    <td>{e.policy_name ?? '—'}</td>
                    <td>{e.device_name ?? '—'}</td>
                    <td className="small muted">{formatDateTime(e.expires_at)}</td>
                    <td className="small muted">{e.created_by_email ?? '—'}</td>
                    <td>
                      {e.status === 'pending' && (
                        <button className="small" onClick={() => revoke(e.id)}>Revoke</button>
                      )}
                      {deleteId === e.id && !tokenIsExpired(e) ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 8 }}>
                          <input
                            style={{ width: 90 }}
                            value={deleteTyped}
                            onChange={(ev) => setDeleteTyped(ev.target.value)}
                            placeholder="delete"
                            aria-label="Type delete to confirm"
                          />
                          <button className="small danger" onClick={() => void removeToken(e)}>Confirm</button>
                          <button className="small" onClick={() => { setDeleteId(null); setDeleteTyped(''); }}>Cancel</button>
                        </span>
                      ) : (
                        <button className="small danger" style={{ marginLeft: 8 }} onClick={() => void removeToken(e)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small muted" style={{ marginBottom: 0 }}>
          Tokens are stored as SHA-256 hashes. The full value is shown once, at generation.
        </p>
      </Panel>
    </>
  );
}
